// =============================================================================
// Pintravel — What the server refused, and what you can do about it
// =============================================================================

import {
  Close as CloseIcon,
  DeleteOutline as DiscardIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { radii } from '@/theme/tokens';

import type { FailedChange } from '@/lib/sync/failed-changes';

interface FailedChangesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Kicks a sync after something is put back in the queue. */
  onRequestSync: () => void;
}

export function FailedChangesDialog({ open, onClose, onRequestSync }: FailedChangesDialogProps) {
  const { t } = useTranslation();
  const [changes, setChanges] = useState<FailedChange[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { listFailedChanges } = await import('@/lib/sync/failed-changes');
    setChanges(await listFailedChanges());
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleConfirmSeparate = useCallback(
    (id: string) => {
      setBusyId(id);
      void (async () => {
        const { confirmSeparatePayment } = await import('@/lib/sync/failed-changes');
        await confirmSeparatePayment(id);
        await load();
        setBusyId(null);
        onRequestSync();
      })();
    },
    [load, onRequestSync],
  );

  const handleRetry = useCallback(
    (id: string) => {
      setBusyId(id);
      void (async () => {
        const { retryFailedChange } = await import('@/lib/sync/failed-changes');
        await retryFailedChange(id);
        await load();
        setBusyId(null);
        // Back in the queue is only half of it — without this the user waits up
        // to thirty seconds for the periodic cycle to notice.
        onRequestSync();
      })();
    },
    [load, onRequestSync],
  );

  const handleDiscard = useCallback(
    (id: string) => {
      setBusyId(id);
      void (async () => {
        const { discardFailedChange } = await import('@/lib/sync/failed-changes');
        await discardFailedChange(id);
        await load();
        setBusyId(null);
      })();
    },
    [load],
  );

  const handleDiscardAll = useCallback(() => {
    void (async () => {
      const { discardAllFailedChanges } = await import('@/lib/sync/failed-changes');
      await discardAllFailedChanges();
      await load();
      onClose();
    })();
  }, [load, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
      >
        {t('sync.failed.title')}
        <IconButton size="small" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('sync.failed.explanation')}
        </Typography>

        {changes === null && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {changes?.length === 0 && (
          <Typography variant="body2" sx={{ py: 2, textAlign: 'center' }}>
            {t('sync.failed.empty')}
          </Typography>
        )}

        {changes && changes.length > 0 && (
          <List disablePadding>
            {changes.map((change) => (
              <ListItem
                key={change.id}
                disableGutters
                sx={{
                  display: 'block',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: `${radii.cardTight}px`,
                  p: 1.5,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  {t(`sync.failed.action.${change.action}`, {
                    entity: t(`sync.failed.entity.${change.entityType}`),
                  })}
                  {change.name && ` „${change.name}”`}
                </Typography>

                {change.reason && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, wordBreak: 'break-word' }}
                  >
                    {change.reason}
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('sync.failed.attempts', { count: change.attempts })}
                  />
                  <Box sx={{ flex: 1 }} />
                  {change.isPossibleDuplicate ? (
                    // A plain retry would meet exactly the same refusal — the
                    // server asked a question, and this is the button that
                    // answers it.
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busyId === change.id}
                      onClick={() => handleConfirmSeparate(change.id)}
                    >
                      {t('sync.failed.separatePayment')}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<RetryIcon fontSize="small" />}
                      disabled={busyId === change.id}
                      onClick={() => handleRetry(change.id)}
                    >
                      {t('sync.failed.retry')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DiscardIcon fontSize="small" />}
                    disabled={busyId === change.id}
                    onClick={() => handleDiscard(change.id)}
                  >
                    {t('sync.failed.discard')}
                  </Button>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {changes && changes.length > 1 && (
          <Button color="error" onClick={handleDiscardAll}>
            {t('sync.failed.discardAll')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
